import { useState, useEffect } from "react";
import { Eye, EyeOff, Car, MapPin, Mail, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { z } from "zod";
import { loginApi, signupApi } from "services/auth/authApis";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Button } from "./ui/button";

// Email validation function
const validateEmail = (email: string) => {
  return (
    email.endsWith("@muj.manipal.edu") || email.endsWith("@jaipur.manipal.edu")
  );
};

// Password validation function
const validatePassword = (password: string) => {
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return {
    isValid:
      hasSpecialChar &&
      password.length >= 8,
    strength: {
      specialChar: hasSpecialChar,
      length: password.length >= 8,
    },
  };
};

// Zod schemas
const loginSchema = z.object({
  email: z
    .string()
    .email()
    .refine((val) => validateEmail(val), {
      message: "Email must end with @muj.manipal.edu or @jaipur.manipal.edu",
    }),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters"),
});

const signupSchema = z.object({
  name: z.string().min(1, "Name is required").max(40, "Name is too long"),
  email: z
    .string()
    .email()
    .refine((val) => validateEmail(val), {
      message: "Email must end with @muj.manipal.edu or @jaipur.manipal.edu",
    }),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .refine(
      (val) => /[!@#$%^&*(),.?":{}|<>]/.test(val),
      "Must contain special character"
    ),
  phoneNo: z
    .string()
    .regex(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  gender: z.enum(["MALE", "FEMALE"], {
    message: "Please select a valid gender",
  }),
  genderPreference: z.enum(["SAME", "ANY"], {
    message: "Please select a valid preference",
  }),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the Terms and Conditions",
  }),
});

type LoginForm = z.infer<typeof loginSchema>;
type SignupForm = z.infer<typeof signupSchema>;

// Storage keys
const LOGIN_FORM_KEY = "loginFormData";
const SIGNUP_FORM_KEY = "signupFormData";

// Helper functions for sessionStorage
const saveToStorage = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving to sessionStorage:", error);
  }
};

const loadFromStorage = (key: string) => {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error loading from sessionStorage:", error);
    return null;
  }
};

const clearFromStorage = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing from sessionStorage:", error);
  }
};

// Login form component
const LoginFields = ({
  loading,
  setLoading,
}: {
  loading: boolean;
  setLoading: (val: boolean) => void;
}) => {
  const navigate = useNavigate();

  // Initialize form with data from sessionStorage
  const [loginForm, setLoginForm] = useState<LoginForm>(() => {
    const saved = loadFromStorage(LOGIN_FORM_KEY);
    return (
      saved || {
        email: "",
        password: "",
      }
    );
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof LoginForm, string>>
  >({});
  const [showPassword, setShowPassword] = useState(false);

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    saveToStorage(LOGIN_FORM_KEY, loginForm);
  }, [loginForm]);

  const validateField = (field: keyof LoginForm, value: any) => {
    try {
      loginSchema.shape[field].parse(value);
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors((prev) => ({
          ...prev,
          [field]: err.issues[0]?.message || "Invalid input",
        }));
      }
    }
  };

  const handleFieldChange = (field: keyof LoginForm, value: any) => {
    setLoginForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    let hasErrors = false;
    Object.keys(loginForm).forEach((field) => {
      try {
        loginSchema.shape[field as keyof LoginForm].parse(
          loginForm[field as keyof LoginForm]
        );
      } catch (err) {
        if (err instanceof z.ZodError) {
          hasErrors = true;
          setErrors((prev) => ({
            ...prev,
            [field]: err.issues[0]?.message || "Invalid input",
          }));
        }
      }
    });

    if (hasErrors) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    try {
      const { email, password } = loginForm;
      setLoading(true);
      await loginApi(email, password);

      // Clear form data from storage on successful login
      clearFromStorage(LOGIN_FORM_KEY);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof LoginForm, string>> = {};
        error.issues.forEach((issue) => {
          const field = issue.path[0] as keyof LoginForm;
          fieldErrors[field] = issue.message;
        });
        setErrors(fieldErrors);
        toast.error(error.issues[0]?.message || "Invalid input");
      } else {
        const errorMessage =
          error.response?.data?.message || "Login failed. Please try again.";
        toast.error(errorMessage);

        if (error.response?.status === 401) {
          setLoginForm((prev) => ({ ...prev, password: "" }));
        }
      }
    } finally {
      if (!document.hidden) {
        setLoading(false);
      }
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <div>
  <label
    htmlFor="login-email"
    className="block mb-2 text-gray-700 font-medium"
  >
    Email <span className="text-red-500">*</span>
  </label>

  <div className="relative group">
    <Mail
      size={19}
      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#f59e42] transition-colors"
    />

    <input
      type="email"
      id="login-email"
      className={`w-full border ${
        errors.email ? "border-red-500" : "border-gray-200"
      } rounded-xl pl-11 pr-4 py-3 bg-gray-50/70 text-gray-800 placeholder:text-gray-400 shadow-[inset_0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-200 hover:bg-white focus:bg-white focus:outline-none focus:border-[#f59e42] focus:ring-4 focus:ring-orange-100`}
      value={loginForm.email}
      onChange={(e) => handleFieldChange("email", e.target.value)}
      onBlur={(e) => validateField("email", e.target.value)}
      placeholder="Enter your MUJ email"
      autoComplete="email"
    />
  </div>

  {errors.email && (
    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
  )}
</div>
      <div>
  <div className="flex items-center justify-between mb-2">
    <label
      htmlFor="login-password"
      className="text-gray-700 font-medium"
    >
      Password <span className="text-red-500">*</span>
    </label>
  </div>

  <div className="relative group">
    <Lock
      size={19}
      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#f59e42] transition-colors"
    />

    <input
      type={showPassword ? "text" : "password"}
      id="login-password"
      className={`w-full border ${
        errors.password ? "border-red-500" : "border-gray-200"
      } rounded-xl pl-11 pr-12 py-3 bg-gray-50/70 text-gray-800 placeholder:text-gray-400 shadow-[inset_0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-200 hover:bg-white focus:bg-white focus:outline-none focus:border-[#f59e42] focus:ring-4 focus:ring-orange-100`}
      value={loginForm.password}
      onChange={(e) => handleFieldChange("password", e.target.value)}
      onBlur={(e) => validateField("password", e.target.value)}
      placeholder="Enter your password"
      autoComplete="current-password"
    />

    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-[#f59e42] hover:bg-orange-50 transition-all"
    >
      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>

  {errors.password && (
    <p className="text-red-500 text-sm mt-1">{errors.password}</p>
  )}

  <div className="mt-2 text-right">
    <button
      type="button"
      className="text-sm text-orange-600 hover:text-orange-700 hover:underline transition-colors"
      onClick={() => navigate("/forgot-password")}
      tabIndex={0}
    >
      Forgot password?
    </button>
  </div>
</div>
      <Button
        type="submit"
        size={"lg"}
        className={`w-full mt-2 hover:bg-amber-500 text-white font-semibold py-2 rounded-md transition duration-200 ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
        disabled={loading}
      >
        {loading ? "Logging in..." : "Login"}
      </Button>
    </form>
  );
};

// Register form component
const RegisterFields = ({
  loading,
  setLoading,
  onRegistered,
}: {
  loading: boolean;
  setLoading: (val: boolean) => void;
  onRegistered: () => void;
}) => {
  // Initialize form with data from sessionStorage
  const [signupForm, setSignupForm] = useState<SignupForm>(() => {
    const saved = loadFromStorage(SIGNUP_FORM_KEY);
    return (
      saved || {
        name: "",
        email: "",
        password: "",
        phoneNo: "",
        gender: "" as "MALE" | "FEMALE",
        genderPreference: "" as "SAME" | "ANY",
        acceptTerms: false,
      }
    );
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof SignupForm, string>>
  >({});
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    saveToStorage(SIGNUP_FORM_KEY, signupForm);
  }, [signupForm]);

  const validateField = (field: keyof SignupForm, value: any) => {
    try {
      signupSchema.shape[field].parse(value);
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors((prev) => ({
          ...prev,
          [field]: err.issues[0]?.message || "Invalid input",
        }));
      }
    }
  };

  const handleFieldChange = (field: keyof SignupForm, value: any) => {
    setSignupForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    let hasErrors = false;
    Object.keys(signupForm).forEach((field) => {
      try {
        signupSchema.shape[field as keyof SignupForm].parse(
          signupForm[field as keyof SignupForm]
        );
      } catch (err) {
        if (err instanceof z.ZodError) {
          hasErrors = true;
          setErrors((prev) => ({
            ...prev,
            [field]: err.issues[0]?.message || "Invalid input",
          }));
        }
      }
    });

    if (hasErrors) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    try {
      // Validate form data
      signupSchema.parse(signupForm);

      const { name, email, password, phoneNo, gender, genderPreference } =
        signupForm;
      setLoading(true);

      let cleanPhone = phoneNo.replace(/\D/g, "");
      if (cleanPhone.length > 10) {
        cleanPhone = cleanPhone.slice(-10);
      }

      const response = await signupApi(
        name,
        email,
        password,
        cleanPhone,
        gender,
        genderPreference
      );
      setLoading(false);

      if (response) {
        // Clear form and storage on successful registration
        const emptyForm = {
          name: "",
          email: "",
          password: "",
          phoneNo: "",
          gender: "" as "MALE" | "FEMALE",
          genderPreference: "" as "SAME" | "ANY",
          acceptTerms: false,
        };
        setSignupForm(emptyForm);
        clearFromStorage(SIGNUP_FORM_KEY);

        toast((t) => (
          <span className="flex flex-col gap-2">
            Verification email sent! Please check your inbox or All Mail folder.
            <button
              className="bg-zinc-300 p-2 cursor-pointer"
              onClick={() => toast.dismiss(t.id)}
            >
              Dismiss
            </button>
          </span>
        ));
        if (onRegistered) onRegistered();
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        // Transform Zod errors into a readable format
        const fieldErrors: Partial<Record<keyof SignupForm, string>> = {};
        err.issues.forEach((issue) => {
          const field = issue.path[0] as keyof SignupForm;
          fieldErrors[field] = issue.message;
        });
        setErrors(fieldErrors);

        // Show first error as toast
        const firstIssue = err.issues[0];
        toast.error(firstIssue?.message || "Invalid input");
      } else {
        toast.error("An error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <div>
        <label
          htmlFor="signup-name"
          className="block mb-1 text-gray-700 font-medium"
        >
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="signup-name"
          className={`w-full border ${errors.name ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400`}
          value={signupForm.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          onBlur={(e) => validateField("name", e.target.value)}
          placeholder="Enter your name"
          autoComplete="name"
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name}</p>
        )}
      </div>
      <div>
        <label
          htmlFor="signup-email"
          className="block mb-1 text-gray-700 font-medium"
        >
          Email{" "}
          <span className="text-gray-400">( MUJ official email address )</span>{" "}
          <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="signup-email"
          className={`w-full border ${errors.email ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400`}
          value={signupForm.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          onBlur={(e) => validateField("email", e.target.value)}
          placeholder="Enter your email"
          autoComplete="email"
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
        )}
      </div>
      <div>
        <label
          htmlFor="signup-password"
          className="block mb-1 text-gray-700 font-medium"
        >
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
          type={showSignupPassword ? "text" : "password"}
          id="signup-password"
          className={`w-full border ${
            errors.password ? "border-red-500" : "border-gray-300"
          } rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-400`}
          value={signupForm.password}
          onChange={(e) => handleFieldChange("password", e.target.value)}
          onBlur={(e) => validateField("password", e.target.value)}
          placeholder="Enter your password"
          autoComplete="new-password"
          />
          <button
          type="button"
          onClick={() => setShowSignupPassword(!showSignupPassword)}
          className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
          >
            {showSignupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
            </div>

        {signupForm.password && (
          <div className="mt-2 text-sm">
            <p className="font-medium mb-1">Password must contain:</p>
            <ul className="space-y-1">
              {[
                { check: /.{8,}/, text: "At least 8 characters" },
                { check: /[!@#$%^&*(),.?":{}|<>]/, text: "Special character" },
              ].map(({ check, text }) => (
                <li key={text} className="flex items-center">
                  <span
                    className={
                      check.test(signupForm.password)
                        ? "text-green-500"
                        : "text-red-500"
                    }
                  >
                    {check.test(signupForm.password) ? "✓" : "✗"}
                  </span>
                  <span className="ml-2">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {errors.password && (
          <p className="text-red-500 text-sm mt-1">{errors.password}</p>
        )}
      </div>
      <div>
        <label
          htmlFor="signup-phoneNo"
          className="block mb-1 text-gray-700 font-medium"
        >
          Phone Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          id="signup-phoneNo"
          className={`w-full border ${errors.phoneNo ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400`}
          value={signupForm.phoneNo}
          onChange={(e) => handleFieldChange("phoneNo", e.target.value)}
          onBlur={(e) => validateField("phoneNo", e.target.value)}
          placeholder="Enter your phone number"
          minLength={10}
          maxLength={10}
          autoComplete="tel"
        />
        {errors.phoneNo && (
          <p className="text-red-500 text-sm mt-1">{errors.phoneNo}</p>
        )}
      </div>
      <div>
        <label
          htmlFor="signup-gender"
          className="block mb-1 text-gray-700 font-medium"
        >
          Gender <span className="text-red-500">*</span>
        </label>
        <select
          id="signup-gender"
          className={`w-full border ${errors.gender ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400`}
          value={signupForm.gender}
          onChange={(e) =>
            handleFieldChange("gender", e.target.value as "MALE" | "FEMALE")
          }
          onBlur={(e) =>
            validateField("gender", e.target.value as "MALE" | "FEMALE")
          }
        >
          <option value="">Select gender</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
        </select>
        {errors.gender && (
          <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
        )}
      </div>
      <div>
        <label
          htmlFor="signup-genderPreference"
          className="block mb-1 text-gray-700 font-medium"
        >
          Who do you prefer to travel with?<span className="text-red-500">*</span>
        </label>
        <select
          id="signup-genderPreference"
          className={`w-full border ${errors.genderPreference ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400`}
          value={signupForm.genderPreference}
          onChange={(e) =>
            handleFieldChange(
              "genderPreference",
              e.target.value as "SAME" | "ANY"
            )
          }
          onBlur={(e) =>
            validateField("genderPreference", e.target.value as "SAME" | "ANY")
          }
        >
          <option value="">Select preference</option>
          <option value="SAME">My gender only</option>
          <option value="ANY">Any gender</option>
        </select>
        {errors.genderPreference && (
          <p className="text-red-500 text-sm mt-1">{errors.genderPreference}</p>
        )}
      </div>
      <div className="flex items-start mt-2">
        <input
          type="checkbox"
          id="signup-acceptTerms"
          checked={signupForm.acceptTerms}
          onChange={(e) => {
            setSignupForm({ ...signupForm, acceptTerms: e.target.checked });
            setErrors({ ...errors, acceptTerms: undefined });
          }}
          className="mr-2 mt-1"
        />
        <label htmlFor="signup-acceptTerms" className="text-sm select-none">
          I accept the{" "}
          <a href="/terms-and-conditions" className="text-orange underline">
            Terms and Conditions
          </a>
          {errors.acceptTerms && (
            <p className="text-red-500 text-sm mt-1">{errors.acceptTerms}</p>
          )}
        </label>
      </div>
      <Button
        type="submit"
        size={"lg"}
        className={`w-full mt-2 hover:bg-amber-500 text-white font-semibold py-2 rounded-md transition duration-200 ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
        disabled={loading}
      >
        {loading ? "Registering..." : "Register"}
      </Button>
    </form>
  );
};

export const LoginForm = () => {
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, [tab]);

  return (
  <div className="relative w-full">

    {/*TRAVEL CAR ANIMATION*/}
    <div className="fixed bottom-0 left-0 z-0 w-[30vw] max-w-[400px] min-w-[260px] pointer-events-none">

      <svg
        viewBox="0 0 500 120"
        className="w-full h-[90px] overflow-visible"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          id="travel-route"
          d="
            M 0 82
            C 65 55, 105 100, 170 70
            C 230 42, 270 42, 325 70
            C 385 100, 430 100, 500 42
          "
          stroke="#f59e42"
          strokeWidth="3.5"
          strokeDasharray="8 10"
          strokeLinecap="round"
        />
      </svg>

      {/* Starting Location */}
      <div className="absolute left-[12%] bottom-[27px]">
        <div className="relative">
          <MapPin
            size={34}
            fill="#f59e42"
            color="white"
            strokeWidth={2.5}
          />

          {/* Pin center */}
          <div
            className="
              absolute left-1/2 top-[8px]
              -translate-x-1/2
              w-[8px] h-[8px]
              rounded-full
              bg-white
            "
          />
        </div>
      </div>

<div className="absolute left-[46%] bottom-[45px] z-10">
  <Car
    size={30}
    strokeWidth={3}
    className="text-[#f59e42]"
    style={{
      filter:
        "drop-shadow(4px 0 0 white) drop-shadow(-1px 0 0 white) drop-shadow(0 1px 0 white) drop-shadow(0 -1px 0 white)",
    }}
  />
</div>

      {/* Destination */}
      <div className="absolute right-[4%] bottom-[32px]">
        <div className="relative">

          <MapPin
            size={34}
            fill="#f59e42"
            color="white"
            strokeWidth={2.5}
          />

          {/* Pin center */}
          <div
            className="
              absolute left-1/2 top-[8px]
              -translate-x-1/2
              w-[8px] h-[8px]
              rounded-full
              bg-white
            "
          />

        </div>
      </div>

    </div>

    <div className="relative z-10 w-full max-w-md p-6 sm:p-8 rounded-lg transition-all duration-500 ease-in-out">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="flex justify-center w-full gap-2 mb-6 bg-white shadow-md">
            <TabsTrigger
              value="login"
              className="px-6 py-2 text-base font-semibold transition-colors duration-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 data-[active=true]:bg-amber-500 data-[active=true]:text-white data-[active=true]:shadow-md"
              data-active={tab === "login"}
              style={{ minWidth: 100, maxWidth: 160 }}
            >
              Login
            </TabsTrigger>
            <TabsTrigger
              value="register"
              className="px-6 py-2 text-base font-semibold transition-colors duration-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 data-[active=true]:bg-amber-500 data-[active=true]:text-white data-[active=true]:shadow-md"
              data-active={tab === "register"}
              style={{ minWidth: 100, maxWidth: 160 }}
            >
              Register
            </TabsTrigger>
          </TabsList>
          <style>{`
  [data-active="true"] {
    background-color: #f59e42 !important;
    color: white !important;
    border-radius: 0.375rem;
    box-shadow: 0 2px 8px 0 rgba(245,158,66,0.10);
  }

  .bg-amber-500 {
    background-color: #f59e42 !important;
  }

  .hover\\:bg-amber-600:hover {
    background-color: #d97706 !important;
  } 
`}</style>
          <TabsContent value="login">
            <LoginFields loading={loading} setLoading={setLoading} />
          </TabsContent>
          <TabsContent value="register">
            <RegisterFields
              loading={loading}
              setLoading={setLoading}
              onRegistered={() => setTab("login")}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};